import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  TextField, 
  Button, 
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  Phone, 
  Email, 
  LocationOn, 
  Schedule, 
  WhatsApp,
  Send,
  CheckCircle
} from '@mui/icons-material';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aquí se enviaría el formulario a un backend
    console.log('Form data:', formData);
    setSnackbar({
      open: true,
      message: 'Mensaje enviado exitosamente. Nos pondremos en contacto contigo pronto.',
      severity: 'success'
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: <Phone color="primary" />,
      title: 'Teléfono',
      content: '+54 11 1234-5678',
      subtitle: 'Línea principal'
    },
    {
      icon: <WhatsApp color="primary" />,
      title: 'WhatsApp',
      content: '+54 9 11 1234-5678',
      subtitle: 'Respuesta rápida'
    },
    {
      icon: <Email color="primary" />,
      title: 'Email',
      content: 'info@clinicadentalsanmartin.com',
      subtitle: 'Consulta general'
    },
    {
      icon: <LocationOn color="primary" />,
      title: 'Dirección',
      content: 'Av. San Martín 1234, Buenos Aires',
      subtitle: 'CABA, Argentina'
    }
  ];

  const businessHours = [
    { day: 'Lunes', hours: '8:00 - 18:00' },
    { day: 'Martes', hours: '8:00 - 18:00' },
    { day: 'Miércoles', hours: '8:00 - 18:00' },
    { day: 'Jueves', hours: '8:00 - 18:00' },
    { day: 'Viernes', hours: '8:00 - 18:00' },
    { day: 'Sábado', hours: '8:00 - 12:00' },
    { day: 'Domingo', hours: 'Cerrado' }
  ];

  const emergencyInfo = [
    'Emergencias dentales 24/7',
    'Atención inmediata para dolor agudo',
    'Traumatismos dentales',
    'Infecciones bucales urgentes'
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: '#946f56',
          color: 'white',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            CONTÁCTANOS
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Estamos aquí para ayudarte con tu salud bucal
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {/* Contact Form */}
          <Box sx={{ flex: '1 1 500px' }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Envíanos un mensaje
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Completa el formulario y nos pondremos en contacto contigo en las próximas 24 horas.
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      required
                      fullWidth
                      label="Nombre completo"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <TextField
                      required
                      fullWidth
                      label="Asunto"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <TextField
                      required
                      fullWidth
                      label="Mensaje"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<Send />}
                      sx={{ py: 1.5 }}
                    >
                      Enviar Mensaje
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: '1 1 500px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Contact Details */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  Información de Contacto
                </Typography>
                <List>
                  {contactInfo.map((info, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon>
                        {info.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={info.title}
                        secondary={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {info.content}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {info.subtitle}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Business Hours */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  Horarios de Atención
                </Typography>
                <List dense>
                  {businessHours.map((schedule, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText
                        primary={schedule.day}
                        secondary={schedule.hours}
                        primaryTypographyProps={{ fontWeight: 'medium' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Emergency Info */}
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2, backgroundColor: 'error.light', color: 'white' }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  🚨 Emergencias 24/7
                </Typography>
                <List dense sx={{ color: 'white' }}>
                  {emergencyInfo.map((info, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ color: 'white', minWidth: 32 }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText primary={info} />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2, backgroundColor: 'white', color: 'error.main' }}
                  startIcon={<Phone />}
                >
                  Llamar Emergencias
                </Button>
              </Paper>
            </Box>
          </Box>
        </Box>

        {/* Map Section */}
        <Box sx={{ mt: 6 }}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom textAlign="center">
              Ubicación
            </Typography>
            <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
              Nuestra clínica está ubicada en el corazón de Buenos Aires, fácilmente accesible en transporte público
            </Typography>
            
            {/* Aquí se integraría un mapa real como Google Maps */}
            <Box
              sx={{
                height: 400,
                backgroundColor: 'grey.200',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed',
                borderColor: 'grey.400'
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <LocationOn sx={{ fontSize: 60, color: 'grey.500', mb: 2 }} />
                <Typography variant="h6" color="grey.600">
                  Mapa de Ubicación
                </Typography>
                <Typography variant="body2" color="grey.500">
                  Av. San Martín 1234, Buenos Aires, Argentina
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 